<template>
  <div class="flex flex-col">
    <div class="relative">
      <input :value="sliderValueFrom"
             ref="fromSlider"
             name="fromSlider"
             type="range"
             :min="minFrom"
             :max="maxTo"
             :disabled="isDisabled"
             @mouseenter="handleSliderHover('from')"
             @mouseleave="handleSliderHover('from')"
      />
      <output v-show="sliderHovered.from"
              ref="fromSliderTooltip"
              for="fromSlider"
              :style="`width: ${tooltipWidth}px`"
              class="absolute rounded pt-1 pb-0.5 text-center text-xs bg-soft border border-solid border-primary-medium"
      >
        {{ formatter(sliderValueFrom) }}
      </output>

      <input :value="sliderValueTo"
             ref="toSlider"
             name="toSlider"
             type="range"
             :min="minFrom"
             :max="maxTo"
             :disabled="isDisabled"
             @mouseenter="handleSliderHover('to')"
             @mouseleave="handleSliderHover('to')"
      />
      <output v-show="sliderHovered.to"
              ref="toSliderTooltip"
              for="toSlider"
              :style="`width: ${tooltipWidth}px`"
              class="pt-1 pb-0.5 absolute rounded text-center text-xs bg-soft border border-solid border-primary-medium"
      >
        {{ formatter(sliderValueTo) }}
      </output>
    </div>
  </div>
</template>

<script>
import tailwind from '../../../tailwind.config'

export default {
  name: 'RangeSlider',

  props: {
    minFrom: {
      type: Number,
      default: 0
    },
    maxTo: {
      type: Number,
      default: 100
    },
    valueFrom: {
      type: Number,
      default: 0
    },
    valueTo: {
      type: Number,
      default: 100
    },
    formatter: {
      type: Function,
      default: (value) => value
    },
    minRange: {
      type: Number,
      default: 1
    },
    tooltipWidth: {
      type: [String, Number],
      default: 160
    },
    isDisabled: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      sliderColor: tailwind.theme.colors['gray-a10'],
      rangeColor: tailwind.theme.colors['primary-a20'],
      fromSlider: null,
      fromSliderTooltip: null,
      toSlider: null,
      toSliderTooltip: null,
      sliderHovered: { from: false, to: false },
      sliderValueFrom: this.valueFrom,
      sliderValueTo: this.valueTo
    }
  },

  watch: {
    valueFrom: {
      immediate: false,
      handler(valueFrom) {
        this.controlFromSlider(valueFrom)
      }
    },
    valueTo: {
      immediate: false,
      handler(valueTo) {
        this.controlToSlider(valueTo)
      }
    }
  },

  methods: {
    controlFromSlider(valueFrom) {
      this.sliderValueFrom = valueFrom
      if (this.sliderValueFrom >= this.sliderValueTo - this.minRange) {
        this.sliderValueFrom = this.sliderValueTo - this.minRange
      }

      this.renderSlider()
    },

    controlToSlider(valueTo) {
      this.sliderValueTo = valueTo
      this.setToggleAccessible()
      if (this.sliderValueTo <= this.sliderValueFrom + this.minRange) {
        this.sliderValueTo = this.sliderValueFrom + this.minRange
      }

      this.renderSlider()
    },

    renderSlider() {
      const rangeDistance = this.maxTo - this.minFrom
      const fromPosition = this.sliderValueFrom - this.minFrom
      const toPosition = this.sliderValueTo - this.minFrom
      const fromSliderWidth = this.toSlider.clientWidth
      const toSliderWidth = this.toSlider.clientWidth
      this.fillSlider(rangeDistance, fromPosition, toPosition, fromSliderWidth, toSliderWidth)
      this.setTooltipPosition(rangeDistance, fromPosition, toPosition, fromSliderWidth, toSliderWidth)
    },

    fillSlider(rangeDistance, fromPosition, toPosition, fromSliderWidth, toSliderWidth) {
      this.toSlider.style.background = `linear-gradient(
      to right,
      ${this.sliderColor} 0%,
      ${this.sliderColor} ${(fromPosition) / (rangeDistance) * fromSliderWidth}px,
      ${this.rangeColor} ${((fromPosition) / (rangeDistance)) * fromSliderWidth}px,
      ${this.rangeColor} ${(toPosition) / (rangeDistance) * toSliderWidth}px,
      ${this.sliderColor} ${(toPosition) / (rangeDistance) * toSliderWidth}px,
      ${this.sliderColor} 100%)`
    },

    setTooltipPosition(rangeDistance, fromPosition, toPosition, fromSliderWidth, toSliderWidth) {
      const fromHandlePosition = fromPosition / rangeDistance
      const toHandlePosition = toPosition / rangeDistance
      const getOffset = (position) => position > 0.5 ? this.tooltipWidth : 0
      this.fromSliderTooltip.style.left = `${fromHandlePosition * toSliderWidth - getOffset(fromHandlePosition)}px`
      this.toSliderTooltip.style.left = `${toHandlePosition * toSliderWidth - getOffset(toHandlePosition)}px`
      this.fromSliderTooltip.style.top = '15px'
      this.toSliderTooltip.style.top = '15px'
    },

    setToggleAccessible() {
      if (!this.toSlider) return

      if (Number(this.toSlider.value) <= 0) {
        this.toSlider.style.zIndex = 2
      } else {
        this.toSlider.style.zIndex = 0
      }
    },

    handleSliderHover(sliderName) {
      this.sliderHovered[sliderName] = !this.sliderHovered[sliderName]
    },

    emitChange() {
      this.$emit('change-range', this.sliderValueFrom, this.sliderValueTo)
    }
  },

  mounted() {
    this.fromSlider = this.$refs.fromSlider
    this.fromSliderTooltip = this.$refs.fromSliderTooltip
    this.toSlider = this.$refs.toSlider
    this.toSliderTooltip = this.$refs.toSliderTooltip
    this.fromSlider.oninput = (e) => this.controlFromSlider(+e.target.value)
    this.toSlider.oninput = (e) => this.controlToSlider(+e.target.value)
    this.fromSlider.onchange = () => this.emitChange()
    this.toSlider.onchange = () => this.emitChange()

    window.addEventListener('resize', this.renderSlider)

    this.sliderValueFrom = this.minFrom
    this.sliderValueTo = this.maxTo
    this.renderSlider()
    this.setToggleAccessible()
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.renderSlider)
  }
}
</script>

<style lang="scss">
input[type=range]::-webkit-slider-thumb {
  @apply bg-primary w-5 h-5 rounded-full cursor-pointer;
  theme: none;
  pointer-events: all;
}

input[type=range]::-moz-range-thumb {
  @apply bg-primary w-5 h-5 rounded-full cursor-pointer;
  theme: none;
  pointer-events: all;
}

input[type=range]::-webkit-slider-thumb:hover {
  box-shadow: inset 0 0 3px #7e50FF, 0 0 9px #7e50FF;
}

input[type=range]::-moz-range-thumb:hover {
  box-shadow: inset 0 0 3px #7e50FF, 0 0 9px #7e50FF;
}

input[type="range"] {
  @apply h-2 rounded bg-gray-a10 w-full absolute;
  theme: none;
  pointer-events: none;
}
</style>
